import { detectMultiConditionOutliers } from '../utils/statistics';

/**
 * Centralized chart configuration constants.
 * Provides standard margins, tick rotations, animations, and tooltip widths.
 */

// ============================================
// Chart Margins
// ============================================

/**
 * Standard margin presets for different chart types
 * Each preset provides top, right, bottom, left values
 */
export const CHART_MARGINS = {
  // Line charts with bottom legend
  LINE_DEFAULT: { top: 0, right: 20, bottom: 50, left: 40 },
  LINE_WITH_LEGEND: { top: 0, right: 20, bottom: 50, left: 55 },

  // Line charts with icon layers (growth curves)
  LINE_GROWTH: { top: 60, right: 20, bottom: 50, left: 75 },

  // Scatter plots (need more left margin for labels)
  SCATTER: { top: 20, right: 30, bottom: 52, left: 90 },

  // Bar charts
  BAR_HORIZONTAL: { top: 35, right: 0, bottom: 45, left: 40 },
  BAR_VERTICAL: { top: 35, right: 20, bottom: 50, left: 60 },

  // Compact charts (embedded in dashboards)
  COMPACT: { top: 10, right: 10, bottom: 30, left: 35 },

  // Full-width charts with legends
  FULL_WITH_LEGEND: { top: 20, right: 20, bottom: 60, left: 60 },
};

// ============================================
// Chart Label Sizing
// ============================================

export const CHART_LABEL_FONT_SIZES = {
  BAR_INSIDE: 9,
  BAR_INSIDE_COMPACT: 8,
  ANNOTATION: 10,
  TINY_ANNOTATION: 6,
};

export const EXPORT_CHART_TYPOGRAPHY = Object.freeze({
  valueLabelFontSizes: Object.freeze([16, 15, 14, 13, 12, 11, 10]),
  axisTickFontSize: 15,
  axisLegendFontSize: 16,
  axisTickPadding: 9,
});

export const CHART_LABEL_FIT = {
  BAR_HORIZONTAL_PADDING: 3,
  BAR_VERTICAL_PADDING: 1,
  TEXT_WIDTH_EM: 0.56,
  BOLD_TEXT_WIDTH_EM: 0.6,
};

/**
 * Get chart margins with optional overrides
 * @param {string} preset - One of CHART_MARGINS keys
 * @param {Object} overrides - Optional margin overrides
 * @returns {Object} Final margin object
 */
export const getChartMargins = (preset, overrides = {}) => ({
  ...(CHART_MARGINS[preset] || CHART_MARGINS.LINE_DEFAULT),
  ...overrides,
});

// ============================================
// Tick Rotation
// ============================================

/**
 * Breakpoints for tick rotation decisions
 */
export const TICK_ROTATION_BREAKPOINTS = {
  MOBILE: 600,
  TABLET: 900,
  DESKTOP: 1200,
};

/**
 * Get appropriate tick rotation based on window width and chart type
 * @param {number} windowWidth - Current window width
 * @param {string} type - Chart type: 'date', 'category', 'ticker'
 * @returns {number} Rotation angle in degrees
 */
export const getTickRotation = (windowWidth, type = 'date') => {
  switch (type) {
    case 'date':
      // Dates rotate at smaller widths
      return windowWidth < TICK_ROTATION_BREAKPOINTS.MOBILE ? -45 : 0;

    case 'category':
      // Categories might need rotation at medium widths too
      return windowWidth < TICK_ROTATION_BREAKPOINTS.TABLET ? -45 : 0;

    case 'ticker':
      // Ticker symbols often need rotation for bar charts
      return windowWidth < TICK_ROTATION_BREAKPOINTS.DESKTOP ? -50 : -45;

    default:
      return windowWidth < TICK_ROTATION_BREAKPOINTS.MOBILE ? -45 : 0;
  }
};

/**
 * Get number of ticks based on window width
 * @param {number} windowWidth - Current window width
 * @param {string} type - 'sparse' | 'normal' | 'dense'
 * @returns {number} Number of ticks to display
 */
export const getTickCount = (windowWidth, type = 'normal') => {
  const baseTicks = Math.max(2, Math.floor(windowWidth / 100));

  switch (type) {
    case 'sparse':
      return Math.min(baseTicks, windowWidth < TICK_ROTATION_BREAKPOINTS.MOBILE ? 5 : 8);
    case 'dense':
      return Math.max(baseTicks, 10);
    case 'normal':
    default:
      return windowWidth < TICK_ROTATION_BREAKPOINTS.MOBILE ? 5 : 10;
  }
};

// ============================================
// Animation Configurations
// ============================================

/**
 * Standard Nivo motion configurations
 * 'gentle' - Slower, smoother (good for initial load)
 * 'stiff' - Quick, snappy (good for data updates)
 * 'wobbly' - Playful bounce effect
 * 'slow' - Very slow for emphasis
 */
export const ANIMATION_CONFIGS = {
  // Use for initial data load, area charts
  GENTLE: 'gentle',

  // Use for bar charts, frequent updates
  STIFF: 'stiff',

  // Use for playful interactions
  WOBBLY: 'wobbly',

  // Use when you want to draw attention
  SLOW: 'slow',

  // Disable animations (for performance-critical views)
  NONE: false,
};

/**
 * Get recommended animation config based on context
 * @param {string} chartType - 'line', 'bar', 'scatter', 'area'
 * @param {boolean} isInteractive - Whether chart has frequent user interactions
 * @returns {string|boolean} Motion config value
 */
export const getAnimationConfig = (chartType, isInteractive = false) => {
  if (isInteractive) {
    return ANIMATION_CONFIGS.STIFF;
  }

  switch (chartType) {
    case 'bar':
      return ANIMATION_CONFIGS.STIFF;
    case 'scatter':
      return ANIMATION_CONFIGS.GENTLE;
    case 'line':
    case 'area':
    default:
      return ANIMATION_CONFIGS.GENTLE;
  }
};

// ============================================
// Tooltip Widths
// ============================================

/**
 * Standard tooltip widths for consistency
 */
export const TOOLTIP_WIDTHS = {
  // Single value tooltips
  COMPACT: 120,

  // Standard tooltips with label + value
  STANDARD: 160,

  // Tooltips with company names
  WIDE: 200,

  // Slice tooltips with multiple series
  SLICE: 204,

  // Growth curve tooltips
  GROWTH: 154,
};

// ============================================
// Legend Configurations
// ============================================

/**
 * Standard legend configurations
 */
export const LEGEND_CONFIGS = {
  // Bottom row legend (for line charts)
  BOTTOM_ROW: {
    anchor: 'bottom',
    direction: 'row',
    justify: false,
    translateX: 0,
    translateY: -4,
    itemsSpacing: 0,
    itemDirection: 'left-to-right',
    itemWidth: 124,
    itemHeight: 20,
    itemOpacity: 0.75,
    symbolSize: 12,
    symbolShape: 'circle',
    symbolBorderColor: 'rgba(0, 0, 0, .5)',
    effects: [
      {
        on: 'hover',
        style: {
          itemBackground: 'rgba(0, 0, 0, .03)',
          itemOpacity: 1,
        },
      },
    ],
  },

  // Top-right column legend (for bar charts with multiple keys)
  TOP_RIGHT_COLUMN: {
    dataFrom: 'keys',
    anchor: 'top-right',
    direction: 'column',
    justify: false,
    translateX: 0,
    translateY: -35,
    itemsSpacing: 2,
    itemWidth: 100,
    itemHeight: 16,
    itemDirection: 'left-to-right',
    itemOpacity: 0.85,
    symbolSize: 12,
    symbolShape: 'square',
  },
};

/**
 * Calculate whether legend should be visible based on chart width and data count
 * @param {number} windowWidth - Current window width
 * @param {number} seriesCount - Number of data series
 * @param {number} itemWidth - Width per legend item (default: 114)
 * @returns {boolean} Whether to show legend
 */
export const shouldShowLegend = (windowWidth, seriesCount, itemWidth = 114) => {
  const dynamicThreshold = itemWidth * seriesCount + 40;
  return windowWidth >= dynamicThreshold;
};

// ============================================
// Y-Axis Range Utilities
// ============================================

/**
 * Calculate dynamic min for y-axis based on data range
 * Adds 20% padding below the minimum value
 * @param {Array} data - Array of series with data points
 * @returns {number|string} Minimum value or 'auto'
 */
export const calculateDynamicMin = (data) => {
  let min = Infinity;
  let max = -Infinity;

  data.forEach((serie) => {
    serie.data.forEach((point) => {
      if (typeof point.y === 'number') {
        if (point.y < min) min = point.y;
        if (point.y > max) max = point.y;
      }
    });
  });

  if (min === Infinity || max === -Infinity) return 'auto';

  const range = max - min;
  const calculatedMin = min - 0.2 * range;

  // Ensure the min doesn't go below 0
  return Math.max(calculatedMin, 0);
};

/**
 * Calculate dynamic Y-axis maximum with headroom so lines aren't clipped at the top.
 * Adds ~10% padding above the data max.
 * @param {Array} data - Nivo-formatted data array
 * @returns {number|string} Calculated max or 'auto'
 */
export const calculateDynamicMax = (data) => {
  let min = Infinity;
  let max = -Infinity;

  data.forEach((serie) => {
    serie.data.forEach((point) => {
      if (typeof point.y === 'number') {
        if (point.y < min) min = point.y;
        if (point.y > max) max = point.y;
      }
    });
  });

  if (min === Infinity || max === -Infinity) return 'auto';

  const range = max - min;
  return max + 0.1 * range;
};

/**
 * Calculate max range for scatter plots (capped at 100 for percentages)
 * @param {Array} data - Data array
 * @param {string} axis - 'x' or 'y'
 * @param {number} cap - Maximum cap (default: 100)
 * @returns {number} Max value for axis
 * @deprecated Use calculateScatterRange for better outlier handling
 */
export const calculateScatterMax = (data, axis = 'y', cap = 100) => {
  let maxValue = Number.MIN_VALUE;

  data.forEach((dataset) => {
    dataset.data.forEach((point) => {
      const value = axis === 'x' ? point.x : point.y;
      maxValue = Math.max(maxValue, value);
    });
  });

  const scaled = Math.ceil(maxValue * 1.05);
  return Math.min(scaled, cap);
};

// ============================================
// Scatter Plot Range with Outlier Handling
// ============================================

/**
 * Round a number to a "nice" value for axis labels
 * Handles both percentage (0-1) and whole number ranges
 * @param {number} value - Value to round
 * @param {boolean} roundUp - Whether to round up (true) or down (false)
 * @returns {number} Nice rounded value
 */
export const roundToNiceNumber = (value, roundUp = true) => {
  if (value === 0) return 0;

  const absValue = Math.abs(value);
  const sign = value < 0 ? -1 : 1;

  // For small values (likely percentages in 0-1 range), use finer increments
  if (absValue <= 1) {
    // Nice percentage increments: 0.1, 0.2, 0.25, 0.3, 0.4, 0.5, 0.6, 0.7, 0.75, 0.8, 0.9, 1.0
    const nicePercentages = [0.1, 0.2, 0.25, 0.3, 0.4, 0.5, 0.6, 0.7, 0.75, 0.8, 0.9, 1.0];
    if (roundUp) {
      const nice = nicePercentages.find(n => n >= absValue - 0.001) || 1.0;
      return sign * nice;
    } else {
      const nice = [...nicePercentages].reverse().find(n => n <= absValue + 0.001) || 0.1;
      return sign * nice;
    }
  }

  // For larger values, use magnitude-based rounding
  const magnitude = Math.pow(10, Math.floor(Math.log10(absValue)));

  // Nice intervals with more granularity
  const niceIntervals = [1, 1.5, 2, 2.5, 3, 4, 5, 6, 7, 8, 10];
  const normalized = absValue / magnitude;

  let niceValue;
  if (roundUp) {
    niceValue = niceIntervals.find(n => n >= normalized - 0.001) || 10;
  } else {
    niceValue = [...niceIntervals].reverse().find(n => n <= normalized + 0.001) || 1;
  }

  return sign * niceValue * magnitude;
};

/**
 * Calculate optimal scatter plot range with intelligent outlier handling
 * @param {Array} data - Nivo scatter plot data format [{ id, data: [{x, y, ...}] }]
 * @param {string} axis - 'x' or 'y'
 * @param {Object} options - Configuration options
 * @param {string} options.mode - 'auto' | 'clamp' | 'show-all' (default: 'auto')
 * @param {number} options.iqrMultiplier - IQR multiplier for outlier detection (default: 3.0 for extreme outliers only)
 * @param {number} options.padding - Percentage padding (default: 0.05 = 5%)
 * @param {number} options.maxCap - Hard maximum cap (default: Infinity)
 * @returns {Object} { min, max, clampedPoints, hasOutliers, tickValues }
 */
export const calculateScatterRange = (data, axis = 'y', options = {}) => {
  const {
    mode = 'auto',
    iqrMultiplier = 2.5, // Tighter detection - more outliers shown (was 3.0)
    padding = 0.05,
    maxCap = Infinity,
  } = options;

  // Flatten all points from all datasets
  const allPoints = [];
  data.forEach((dataset) => {
    (dataset.data || []).forEach((point) => {
      if (point && typeof point.x === 'number' && typeof point.y === 'number') {
        allPoints.push(point);
      }
    });
  });

  if (allPoints.length === 0) {
    return {
      min: 0,
      max: 100,
      clampedPoints: [],
      hasOutliers: false,
      tickValues: [0, 20, 40, 60, 80, 100],
    };
  }

  // Extract values for the specified axis
  const values = allPoints.map(p => axis === 'x' ? p.x : p.y);

  // If show-all mode or not enough data for IQR, use simple min/max
  if (mode === 'show-all' || allPoints.length < 4) {
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 10;
    const paddedMin = Math.max(0, min - range * padding);
    const paddedMax = Math.min(maxCap, max + range * padding);

    return {
      min: roundToNiceNumber(paddedMin, false),
      max: roundToNiceNumber(paddedMax, true),
      clampedPoints: [],
      hasOutliers: false,
      tickValues: generateTickValues(paddedMin, paddedMax),
    };
  }

  // Calculate quartiles for outlier detection
  const sorted = [...values].sort((a, b) => a - b);
  const n = sorted.length;
  const midIndex = Math.floor(n / 2);

  const lowerHalf = sorted.slice(0, midIndex);
  const upperHalf = n % 2 === 0 ? sorted.slice(midIndex) : sorted.slice(midIndex + 1);

  const q1 = lowerHalf.length > 0
    ? lowerHalf[Math.floor(lowerHalf.length / 2)]
    : sorted[0];
  const q3 = upperHalf.length > 0
    ? upperHalf[Math.floor(upperHalf.length / 2)]
    : sorted[sorted.length - 1];

  const iqr = q3 - q1;
  const lowerBound = q1 - iqrMultiplier * iqr;
  const upperBound = q3 + iqrMultiplier * iqr;

  // Use shared multi-condition outlier detection
  const { outlierIndices } = detectMultiConditionOutliers(values);
  const inlierValues = [];
  const outlierValues = [];

  values.forEach((v, i) => {
    if (outlierIndices.has(i)) {
      outlierValues.push(v);
    } else {
      inlierValues.push(v);
    }
  });

  const hasOutliers = outlierValues.length > 0;

  // Determine range based on mode
  let rangeMin, rangeMax;

  if (mode === 'clamp' || (mode === 'auto' && hasOutliers)) {
    // Use inlier range with padding
    const inlierMin = inlierValues.length > 0 ? Math.min(...inlierValues) : 0;
    const inlierMax = inlierValues.length > 0 ? Math.max(...inlierValues) : 100;
    const inlierRange = inlierMax - inlierMin || 10;

    rangeMin = Math.max(0, inlierMin - inlierRange * padding);
    rangeMax = inlierMax + inlierRange * padding;

    // Extend slightly beyond to accommodate clamped points visually
    if (hasOutliers) {
      rangeMax = rangeMax * 1.05;
    }
  } else {
    // No outliers or show-all mode - use full range
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 10;

    rangeMin = Math.max(0, min - range * padding);
    rangeMax = max + range * padding;
  }

  // Apply max cap
  rangeMax = Math.min(rangeMax, maxCap);

  // Round to nice numbers
  const niceMin = roundToNiceNumber(rangeMin, false);
  const niceMax = roundToNiceNumber(rangeMax, true);

  // Identify clamped points with their original and clamped values
  // Only mark points that were actually identified as outliers (not just beyond IQR bounds)
  const clampedPoints = [];
  if (mode === 'clamp' || (mode === 'auto' && hasOutliers)) {
    allPoints.forEach((point, index) => {
      const value = axis === 'x' ? point.x : point.y;
      // Check if this specific value was classified as an outlier
      if (outlierValues.includes(value)) {
        const clampedValue = Math.min(Math.max(value, niceMin), niceMax * 0.98);
        clampedPoints.push({
          ...point,
          originalValue: value,
          clampedValue,
          axis,
          index,
          isUpperOutlier: value > upperBound,
          isLowerOutlier: value < lowerBound,
        });
      }
    });
  }

  return {
    min: niceMin,
    max: niceMax,
    clampedPoints,
    hasOutliers,
    tickValues: generateTickValues(niceMin, niceMax),
  };
};

/**
 * Generate evenly spaced tick values for an axis
 * @param {number} min - Minimum value
 * @param {number} max - Maximum value
 * @param {number} count - Approximate number of ticks (default: 5)
 * @returns {number[]} Array of tick values
 */
const generateTickValues = (min, max, count = 5) => {
  const range = max - min;
  if (range <= 0) return [min];

  const rawStep = range / (count - 1);
  const niceStep = roundToNiceNumber(rawStep, true);

  const ticks = [];
  let tick = Math.ceil(min / niceStep) * niceStep;

  while (tick <= max) {
    ticks.push(tick);
    tick += niceStep;
  }

  // Ensure we have at least min and max
  if (ticks.length === 0 || ticks[0] > min) {
    ticks.unshift(roundToNiceNumber(min, false));
  }
  if (ticks[ticks.length - 1] < max) {
    ticks.push(roundToNiceNumber(max, true));
  }

  return ticks;
};
